import React, { useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

const Terms: React.FC = () => {
  const { currentUser } = useAuth();
  const [searchParams] = useSearchParams();
  
  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  
  // Determine back navigation based on referrer or current user status
  const getBackNavigation = () => {
    const from = searchParams.get('from');
    
    if (from === 'landing') {
      return { to: '/', text: 'Back to landing' };
    }
    
    if (currentUser) {
      return { to: '/dashboard', text: 'Back to dashboard' };
    }
    
    return { to: '/signup', text: 'Back to signup' };
  };
  
  const backNav = getBackNavigation();
  
  return (
    <div className="min-h-screen px-6 sm:px-8 py-12 relative">
      {/* Subtle animated background */}
      <div className="absolute inset-0 dot-pattern-animated opacity-25 pointer-events-none" />
      
      <div className="max-w-4xl mx-auto relative z-10">
        {/* Back button */}
        <Link 
          to={backNav.to}
          className="inline-flex items-center text-sm text-primary-600 hover:text-black transition-colors mb-8"
        >
          <ArrowLeft size={16} className="mr-2" />
          {backNav.text}
        </Link>

        {/* Header */}
        <div className="animate-fade-up mb-12">
          <h1 className="text-5xl font-display font-bold tracking-tight mb-4">
            Terms of Service
          </h1>
          <p className="text-xl text-primary-600">
            Last updated: December 2024
          </p>
        </div>

        {/* Content */}
        <div className="animate-fade-up prose prose-lg max-w-none" style={{ animationDelay: '0.1s' }}>
          <div className="bg-white rounded-2xl p-8 shadow-minimal space-y-8">
            
            <section>
              <h2 className="text-2xl font-display font-bold mb-4">1. Acceptance of Terms</h2>
              <p className="text-primary-700 leading-relaxed">
                By accessing and using ClipThis ("the Service"), you accept and agree to be bound by the terms and provision of this agreement. If you do not agree to abide by the above, please do not use this service.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-display font-bold mb-4">2. Description of Service</h2>
              <p className="text-primary-700 leading-relaxed mb-4">
              ClipThis is an AI-powered video analysis and clipping service that helps content creators automatically generate highlights from their video content. Our service includes:
              </p>
              <ul className="list-disc list-inside space-y-2 text-primary-700">
                <li>Automated video analysis using artificial intelligence</li>
                <li>Highlight detection based on audio and visual cues</li>
                <li>Clip generation and export functionality</li>
                <li>User account management and content storage</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-display font-bold mb-4">3. User Accounts</h2>
              <p className="text-primary-700 leading-relaxed mb-4">
                To access certain features of the Service, you must register for an account. You agree to:
              </p>
              <ul className="list-disc list-inside space-y-2 text-primary-700">
                <li>Provide accurate, current, and complete information during registration</li>
                <li>Maintain the security of your password and account</li>
                <li>Notify us immediately of any unauthorized use of your account</li>
                <li>Accept responsibility for all activities under your account</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-display font-bold mb-4">4. Content and Intellectual Property</h2>
              <p className="text-primary-700 leading-relaxed mb-4">
                You retain ownership of any content you upload to ClipThis. By uploading content, you grant us a limited license to:
              </p>
              <ul className="list-disc list-inside space-y-2 text-primary-700">
                <li>Process and analyze your content using our AI systems</li>
                <li>Generate clips and highlights from your content</li>
                <li>Store your content securely on our servers</li>
                <li>Provide the processed content back to you</li>
              </ul>
              <p className="text-primary-700 leading-relaxed mt-4">
                You must have the legal right to upload and process any content you submit to our service.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-display font-bold mb-4">5. Prohibited Uses</h2>
              <p className="text-primary-700 leading-relaxed mb-4">
                You may not use our service to:
              </p>
              <ul className="list-disc list-inside space-y-2 text-primary-700">
                <li>Upload content that violates any laws or regulations</li>
                <li>Process content that contains harmful, abusive, or inappropriate material</li>
                <li>Attempt to reverse engineer or interfere with our AI systems</li>
                <li>Use the service for any commercial purpose without authorization</li>
                <li>Upload content that infringes on others' intellectual property rights</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-display font-bold mb-4">6. Subscription and Billing</h2>
              <p className="text-primary-700 leading-relaxed mb-4">
                Our service offers both free and paid subscription plans:
              </p>
              <ul className="list-disc list-inside space-y-2 text-primary-700">
                <li>Free plans have limited features and processing quotas</li>
                <li>Paid subscriptions are billed monthly or annually</li>
                <li>Subscriptions automatically renew unless cancelled</li>
                <li>Refunds may be available within 14 days of purchase</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-display font-bold mb-4">7. Privacy and Data Protection</h2>
              <p className="text-primary-700 leading-relaxed">
                Your privacy is important to us. Please review our Privacy Policy, which also governs your use of the Service, to understand our practices regarding the collection and use of your personal information.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-display font-bold mb-4">8. Limitation of Liability</h2>
              <p className="text-primary-700 leading-relaxed">
                In no event shall ClipThis be liable for any indirect, incidental, special, consequential, or punitive damages, including without limitation, loss of profits, data, use, goodwill, or other intangible losses, resulting from your use of the service.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-display font-bold mb-4">9. Service Availability</h2>
              <p className="text-primary-700 leading-relaxed">
                We strive to maintain high service availability but do not guarantee uninterrupted access. We reserve the right to modify, suspend, or discontinue the service at any time with reasonable notice.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-display font-bold mb-4">10. Changes to Terms</h2>
              <p className="text-primary-700 leading-relaxed">
                We reserve the right to modify these terms at any time. We will notify users of significant changes via our Twitter account (@A5omic) or through our service. Continued use of the service after changes constitutes acceptance of the new terms.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-display font-bold mb-4">11. Contact Information</h2>
              <p className="text-primary-700 leading-relaxed">
                If you have any questions about these Terms, please contact us at:
              </p>
              <div className="mt-4 p-4 bg-primary-50 rounded-lg">
                <p className="text-primary-700">
                  <a href="https://twitter.com/A5omic" target="_blank" rel="noopener noreferrer" className="text-black hover:underline">
                    DM us on Twitter @A5omic
                  </a>
                </p>
              </div>
            </section>

          </div>
        </div>
      </div>
    </div>
  );
};

export default Terms; 